import { useEffect, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppRoutes } from 'constants/AppRoutes';
import { BLOCKER_STATES } from 'constants/general';
import { SURVEY_QUESTION_TYPES, SURVEY_STATUS } from 'constants/materialAssessment';
import useFieldsErrors from 'hooks/useFieldsErrors';
import i18n from 'i18n';
import {
  useCreateSurveyMutation,
  useGetSingleSurveyQuery,
  useUpdateSurveyMutation,
} from 'store/slices/survey/apis/surveyApi';
import { selectViewMode } from 'store/slices/survey/selectors';
import getRequestDataForSurvey from 'utils/formHelpers/getRequestDataForSurvey';

import { STANDARD_ID } from '../../../../../constants/formFields';
import CreateSurveyCancelModal from './components/CreateSurveyCancelModal/CreateSurveyCancelModal';
import useCreateSurveyForm from './hooks/useCreateSurveyForm';
import CreateSurveyView from './CreateSurveyView';

import type { ReactElement } from 'react';
import type { FieldErrors, SubmitErrorHandler } from 'react-hook-form';
import type IQuestionsItem from '../../../../../store/slices/survey/interfaces/IQuestionsItem';
import type { TCreateSurveySchema } from '../../../../../utils/validators/MASurveyValidators';

function CreateSurvey(): ReactElement {
  const { surveyId } = useParams<{ surveyId: string }>();
  const { recordId } = useParams<{ recordId: string }>();
  const navigate = useNavigate();
  const [showStandards, setShowStandards] = useState(false);
  const isViewMode = useSelector(selectViewMode);

  const [createSurvey, { isLoading: isCreating }] = useCreateSurveyMutation();
  const [updateSurvey, { isLoading: isUpdating }] = useUpdateSurveyMutation();
  const { setFieldsErrors } = useFieldsErrors();

  const isPageLoading = isCreating || isUpdating;

  const {
    control,
    handleSubmit,
    errors,
    reset,
    setError,
    fieldsName,
    setValue,
    isDirty,
  } = useCreateSurveyForm({ surveyId, showStandards, recordId });

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isNavigatingAfterSave, setIsNavigatingAfterSave] = useState(false);
  const [shouldNavigate, setShouldNavigate] = useState<{ path: string | null } | null>(null);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => currentLocation.pathname !== nextLocation.pathname
      && !isCancelModalOpen
      && !isNavigatingAfterSave
      && !shouldNavigate
      && !isViewMode,
  );

  useEffect(() => {
    if (blocker.state === BLOCKER_STATES.blocked && !isNavigatingAfterSave && !shouldNavigate) {
      setIsCancelModalOpen(true);
    }
  }, [blocker, isNavigatingAfterSave, shouldNavigate]);

  useEffect(() => {
    if (isNavigatingAfterSave && blocker.state === BLOCKER_STATES.blocked) {
      blocker.proceed?.();
    }
  }, [isNavigatingAfterSave, blocker]);

  useEffect(() => {
    if (!isCancelModalOpen && shouldNavigate) {
      const navigationPath = shouldNavigate.path;
      setShouldNavigate(null);
      if (blocker.state === BLOCKER_STATES.blocked) {
        blocker.reset?.();
      }
      if (navigationPath) {
        navigate(navigationPath);
      } else {
        navigate(-1);
      }
    }
  }, [isCancelModalOpen, shouldNavigate, blocker, navigate]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'questions',
  });

  const { data: surveyData, isLoading: isSurveyLoading } = useGetSingleSurveyQuery(
    { id: surveyId },
    { skip: !surveyId },
  );

  const standardId = (surveyData?.data as IQuestionsItem)?.standardId;

  useEffect(() => {
    if (!isSurveyLoading && standardId && standardId > 0) {
      setShowStandards(true);
    }
  }, [standardId, isSurveyLoading]);

  const scrollToField = (fieldName: string | null): void => {
    if (!fieldName) {
      return;
    }

    const fieldElement = document.querySelector<HTMLElement>(`[name="${fieldName}"]`);

    if (!fieldElement) {
      return;
    }

    fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    fieldElement.focus({ preventScroll: true });
  };

  const getFirstErrorFieldName = (formErrors: FieldErrors<TCreateSurveySchema>): string | null => {
    if (formErrors.name) {
      return 'name';
    }

    if (formErrors.description) {
      return 'description';
    }

    if (Array.isArray(formErrors.questions)) {
      for (let questionIndex = 0; questionIndex < formErrors.questions.length; questionIndex += 1) {
        const questionErrors = formErrors.questions[questionIndex];

        if (!questionErrors) {
          // eslint-disable-next-line no-continue
          continue;
        }

        if (questionErrors.name) {
          return `questions.${questionIndex}.name`;
        }

        if (questionErrors.questionType) {
          return `questions.${questionIndex}.questionType`;
        }

        if (Array.isArray(questionErrors.options)) {
          for (let optionIndex = 0; optionIndex < questionErrors.options.length; optionIndex += 1) {
            const optionErrors = questionErrors.options[optionIndex];

            if (optionErrors?.name) {
              return `questions.${questionIndex}.options.${optionIndex}.name`;
            }
          }
        }
      }
    }

    return null;
  };

  const handleInvalidSubmit: SubmitErrorHandler<TCreateSurveySchema> = (submitErrors) => {
    const firstErrorFieldName = getFirstErrorFieldName(submitErrors);

    if (firstErrorFieldName) {
      scrollToField(firstErrorFieldName);
    }
  };

  const handleAddQuestion = () => {
    append({
      name: '',
      questionType: SURVEY_QUESTION_TYPES.SHORT_ANSWER,
      isCommentable: false,
      options: [],
    });
  };

  const handleAddStandards = () => {
    setShowStandards(true);
  };

  const handleRemoveStandards = () => {
    setShowStandards(false);
    setValue(STANDARD_ID, 0);
  };

  const onSurveySubmit = (status: string) => handleSubmit(
    (values) => {
      const requestData = getRequestDataForSurvey({
        values,
        surveyId,
        status,
      });

      const surveyEndpoint = surveyId ? updateSurvey : createSurvey;

      surveyEndpoint(requestData as any).unwrap()
        .then(() => {
          toast.success(i18n.t(
            surveyId ? 'notifications.recordHasBeenUpdated'
              : 'notifications.recordHasBeenAdded',
          ));
          reset(undefined, { keepValues: true });
          setIsNavigatingAfterSave(true);
          if (blocker.state === BLOCKER_STATES.blocked) {
            blocker.reset?.();
          }
          navigate(AppRoutes.materialityAssessmentSurveyCreation.replace(':recordId', recordId || ''));
        })
        .catch((errorsResponse) => {
          toast.error(errorsResponse.data.error);
          setFieldsErrors<TCreateSurveySchema>({ errors: errorsResponse.data, setError, fieldsName });
        });
    },
    (submitErrors) => {
      if (Object.keys(submitErrors).length) {
        const firstErrorFieldName = getFirstErrorFieldName(errors);
        scrollToField(firstErrorFieldName);
      } else {
        handleInvalidSubmit(submitErrors);
      }
    },
  );

  const handleSaveAsDraft = () => {
    onSurveySubmit(SURVEY_STATUS.DRAFT)();
  };

  const handleMarkAsDone = () => {
    onSurveySubmit(SURVEY_STATUS.DONE)();
  };

  const handleCancel = () => {
    setIsCancelModalOpen(true);
  };

  const handleBack = () => {
    if (isViewMode) {
      navigate(-1);
      return;
    }

    handleCancel();
  };

  const handleCloseWithoutSaving = () => {
    if (isDirty) {
      reset(undefined, { keepValues: true });
    }

    const currentLocation = blocker?.location?.pathname || null;
    setShouldNavigate({ path: currentLocation });
    setIsCancelModalOpen(false);
  };

  const handleSaveDraftAndClose = () => {
    setIsCancelModalOpen(false);
    handleSaveAsDraft();
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      return '';
    };

    if (isDirty) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  return (
    <>
      <CreateSurveyView
        control={control}
        onBack={handleBack}
        onCancel={handleCancel}
        onSaveAsDraft={handleSaveAsDraft}
        onMarkAsDone={handleMarkAsDone}
        isLoading={isPageLoading}
        isSavingDraft={isCreating || isUpdating}
        isSavingDone={isCreating || isUpdating}
        fields={fields as unknown as { id: string }[]}
        onRemoveQuestion={(index) => remove(index)}
        onAddQuestion={handleAddQuestion}
        hasStandards={showStandards}
        onAddStandards={handleAddStandards}
        onRemoveStandards={handleRemoveStandards}
        standardId={standardId}
        setValue={setValue}
        isViewMode={isViewMode}
      />
      <CreateSurveyCancelModal
        open={isCancelModalOpen}
        onCancel={() => setIsCancelModalOpen(false)}
        onCloseWithoutSaving={handleCloseWithoutSaving}
        onSaveDraft={handleSaveDraftAndClose}
        isLoading={isPageLoading}
      />
    </>
  );
}

export default CreateSurvey;
